// ---------------------------------------------------------------------------
// Client-side attachment size policy + image compression.
//
// Policy (shared by Notes/Tasks attachments AND the ERP Order Details
// "Jewellery Files" uploader):
//   • Images (png/jpg/webp/gif) larger than the cap are auto-compressed
//     (downscale + re-encode to JPEG) until they fit; only blocked if they
//     still can't get under the cap after aggressive compression.
//   • PDFs larger than the cap are blocked (browsers can't reliably shrink
//     them) with a clear "please compress and re-upload" message.
//
// The cap is intentionally centralised here so every uploader stays in sync.
// ---------------------------------------------------------------------------

export const ATTACH_MAX_BYTES = 500 * 1024; // 500 KB
export const ATTACH_CAP_LABEL = '500 KB';

export function formatBytes(n) {
  if (!n || n <= 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export const isImageType = (t) => typeof t === 'string' && t.startsWith('image/');
export const isPdfFile = (f) =>
  f && ((f.type === 'application/pdf') || /\.pdf$/i.test(f.name || ''));

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve({ img, url });
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not read image')); };
    img.src = url;
  });
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve) => {
    try { canvas.toBlob((b) => resolve(b), type, quality); }
    catch { resolve(null); }
  });
}

// Compress an image File/Blob to <= maxBytes by progressively downscaling and
// lowering JPEG quality. Returns the ORIGINAL file untouched when it already
// fits (avoids needless quality loss). Throws Error('image_too_large') if it
// cannot reach the target.
export async function compressImageBlob(file, maxBytes = ATTACH_MAX_BYTES) {
  if (file.size <= maxBytes) return file;
  const { img, url } = await loadImage(file);
  try {
    let width = img.naturalWidth || img.width;
    let height = img.naturalHeight || img.height;
    if (!width || !height) throw new Error('image_too_large');
    // Cap the starting dimension so huge photos converge quickly.
    const MAX_DIM = 2000;
    if (Math.max(width, height) > MAX_DIM) {
      const s = MAX_DIM / Math.max(width, height);
      width = Math.round(width * s);
      height = Math.round(height * s);
    }
    const scales = [1, 0.85, 0.7, 0.55, 0.42, 0.32, 0.24, 0.18];
    const qualities = [0.82, 0.72, 0.62, 0.5, 0.4, 0.32];
    for (const scale of scales) {
      const w = Math.max(1, Math.round(width * scale));
      const h = Math.max(1, Math.round(height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('image_too_large');
      // Flatten any transparency onto white (JPEG has no alpha channel).
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, w, h);
      ctx.drawImage(img, 0, 0, w, h);
      for (const q of qualities) {
        // eslint-disable-next-line no-await-in-loop
        const blob = await canvasToBlob(canvas, 'image/jpeg', q);
        if (blob && blob.size <= maxBytes) return blob;
      }
    }
    throw new Error('image_too_large');
  } finally {
    URL.revokeObjectURL(url);
  }
}

// Wrap a Blob back into a File, keeping the original base name (.jpg extension
// since we always re-encode to JPEG).
export function blobToFile(blob, originalName) {
  const base = (originalName || 'image').replace(/\.[^./\\]+$/, '') || 'image';
  return new File([blob], `${base}.jpg`, { type: blob.type || 'image/jpeg' });
}

export function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error('Could not read file'));
    r.readAsDataURL(blob);
  });
}

// High-level helper for multipart uploaders (Notes / Tasks attachments).
// Returns a File ready to upload (compressed if it was an oversized image).
// Throws an Error whose `.code` is one of:
//   'pdf_too_large'   — a PDF over the cap (blocked, no compression)
//   'image_too_large' — an image that couldn't be compressed under the cap
// and carries `.size` (bytes to report to the user).
export async function prepareAttachmentFile(file, maxBytes = ATTACH_MAX_BYTES) {
  if (isPdfFile(file)) {
    if (file.size > maxBytes) {
      const e = new Error('pdf_too_large'); e.code = 'pdf_too_large'; e.size = file.size; throw e;
    }
    return file;
  }
  if (isImageType(file.type)) {
    if (file.size <= maxBytes) return file;
    try {
      const blob = await compressImageBlob(file, maxBytes);
      if (blob.size > maxBytes) throw new Error('image_too_large');
      return blob instanceof File ? blob : blobToFile(blob, file.name);
    } catch {
      const e = new Error('image_too_large'); e.code = 'image_too_large'; e.size = file.size; throw e;
    }
  }
  // Non image/pdf types are left to the caller's own type validation.
  return file;
}
